import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { ThemeProvider } from "@material-ui/styles";
import Tooltip from "@material-ui/core/Tooltip";
import theme from "../../../../Themes/defaultTheme";

/**
 * Generic Tooltip atom component.
 *
 * Supports standard tooltip usage as well as conditional overflow display
 * (`onlyWhenOverflowing`), where the tooltip is only shown when the wrapped
 * content is actually truncated with ellipsis (`...`).
 */
const A_Tooltip = React.forwardRef((props, ref) => {
  const {
    title,
    children,
    onlyWhenOverflowing = false,
    enterDelay = 500,
    leaveDelay = 200,
    arrow = true,
    placement = "top",
    disableFocusListener = true,
    disableTouchListener = true,
    ...rest
  } = props;

  const localRef = useRef(null);
  const timerRef = useRef(null);
  const [open, setOpen] = useState(false);

  const checkOverflow = useCallback(() => {
    if (!onlyWhenOverflowing) {
      return true;
    }
    const el = localRef.current;
    if (!el) return false;

    // Check if element itself overflows
    const selfOverflow = el.scrollWidth > el.clientWidth;
    // Or if an inner text-holding element (e.g. span) overflows
    const textChild = el.querySelector("span") || el.querySelector("p");
    const childOverflow = textChild
      ? textChild.scrollWidth > textChild.clientWidth
      : false;

    return selfOverflow || childOverflow;
  }, [onlyWhenOverflowing]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  if (!title) {
    return children;
  }

  const handleMouseEnter = (e) => {
    if (children && children.props && children.props.onMouseEnter) {
      children.props.onMouseEnter(e);
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const shouldShow = checkOverflow();
    if (shouldShow) {
      if (enterDelay > 0) {
        timerRef.current = setTimeout(() => {
          setOpen(true);
        }, enterDelay);
      } else {
        setOpen(true);
      }
    }
  };

  const handleMouseLeave = (e) => {
    if (children && children.props && children.props.onMouseLeave) {
      children.props.onMouseLeave(e);
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (leaveDelay > 0) {
      timerRef.current = setTimeout(() => {
        setOpen(false);
      }, leaveDelay);
    } else {
      setOpen(false);
    }
  };

  const handleRef = (node) => {
    localRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref && typeof ref === "object") {
      ref.current = node;
    }

    if (children && typeof children === "object" && children.ref) {
      const { ref: childRef } = children;
      if (typeof childRef === "function") {
        childRef(node);
      } else if (childRef && typeof childRef === "object") {
        childRef.current = node;
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Tooltip
        open={open}
        title={title}
        disableHoverListener
        disableFocusListener={disableFocusListener}
        disableTouchListener={disableTouchListener}
        arrow={arrow}
        placement={placement}
        {...rest}
      >
        {React.isValidElement(children) ? (
          React.cloneElement(children, {
            ref: handleRef,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
          })
        ) : (
          <span
            ref={handleRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
              display: "inline-block",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {children}
          </span>
        )}
      </Tooltip>
    </ThemeProvider>
  );
});

A_Tooltip.displayName = "A_Tooltip";

A_Tooltip.propTypes = {
  title: PropTypes.node,
  children: PropTypes.node.isRequired,
  onlyWhenOverflowing: PropTypes.bool,
  enterDelay: PropTypes.number,
  leaveDelay: PropTypes.number,
  arrow: PropTypes.bool,
  placement: PropTypes.string,
  disableFocusListener: PropTypes.bool,
  disableTouchListener: PropTypes.bool,
};

A_Tooltip.defaultProps = {
  title: "",
  onlyWhenOverflowing: false,
  enterDelay: 500,
  leaveDelay: 200,
  arrow: true,
  placement: "top",
  disableFocusListener: true,
  disableTouchListener: true,
};

export default A_Tooltip;
